import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from "react-router-dom";
import PlantillaScreen from '../../../helpers/PlantillaScreen';
import { LoaderScreen } from '../../../components/LoaderScreen';
import request from '../../../../Helpers/API';
import Swal from 'sweetalert2';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { ButtonBack } from '../Util/ButtonBack';
import { SimpleSelect } from '../../ConfigurarLeads/SimpleSelect';

const TIPO_OPCIONES = [
    { value: "texto", label: "Texto libre" },
    { value: "numero", label: "Número" },
    { value: "fecha", label: "Fecha" },
    { value: "lista", label: "Lista desplegable" },
];

// campo (llave técnica) = etiqueta convertida a snake_case sin acentos, para
// que el usuario nunca tenga que pensar en un "nombre de campo" -- solo
// escribe cómo quiere que se vea (etiqueta) y aquí se deriva automáticamente.
const slugify = (texto) =>
    (texto || "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

const campoVacio = () => ({
    _key: Math.random().toString(36).slice(2),
    campo: "",
    etiqueta: "",
    tipo: "texto",
    requerido: false,
    opciones: [],
});

// Pantalla para que un administrador configure, sin desarrollo, qué campos
// adicionales debe capturar el usuario al levantar un ticket en una sala en
// particular (ej. sala "Confirmación de depósitos" -> campo "Banco"). Se
// guarda como JSON en Cat_Sala.camposExtra; el formulario de alta de
// tickets (SeguimientoActividadesNuevaScreen.js) lo lee y pinta los campos
// dinámicamente según la sala elegida.
export const CatSalaCamposExtraScreen = () => {

    const history = useHistory();
    const { id } = useParams();

    const [nombreSala, setNombreSala] = useState("");
    const [campos, setCampos] = useState([]);
    const [opcionTexto, setOpcionTexto] = useState({});
    const [loader, setLoader] = useState({ mostrar: false });
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (!id) return;

        setLoader({ mostrar: true });

        request
            .get(`Sala/CamposExtra/${id}`)
            .then((result) => {
                const data = result?.data ?? result;
                setNombreSala(data?.nombresala || data?.nombreSala || "");

                let parsed = [];
                try {
                    parsed = data?.camposextra || data?.camposExtra
                        ? JSON.parse(data.camposextra || data.camposExtra)
                        : [];
                } catch {
                    parsed = [];
                }

                setCampos(
                    (parsed || []).map((c) => ({
                        _key: Math.random().toString(36).slice(2),
                        campo: c.campo || slugify(c.etiqueta),
                        etiqueta: c.etiqueta || "",
                        tipo: c.tipo || "texto",
                        requerido: !!c.requerido,
                        opciones: Array.isArray(c.opciones) ? c.opciones : [],
                    }))
                );
            })
            .catch(() => {
                Swal.fire("ERROR", "No se pudieron cargar los campos de la sala", "error");
            })
            .finally(() => setLoader({ mostrar: false }));
    }, [id]);

    const onAgregarCampo = () => {
        setCampos((prev) => [...prev, campoVacio()]);
    };

    const onQuitarCampo = (key) => {
        setCampos((prev) => prev.filter((c) => c._key !== key));
    };

    const onCambiarEtiqueta = (key, etiqueta) => {
        setCampos((prev) =>
            prev.map((c) => (c._key === key ? { ...c, etiqueta, campo: slugify(etiqueta) } : c))
        );
    };

    const onCambiarTipo = (key, tipo) => {
        setCampos((prev) => prev.map((c) => (c._key === key ? { ...c, tipo } : c)));
    };

    const onCambiarRequerido = (key, requerido) => {
        setCampos((prev) => prev.map((c) => (c._key === key ? { ...c, requerido } : c)));
    };

    const onAgregarOpcion = (key) => {
        const texto = (opcionTexto[key] || "").trim();
        if (!texto) return;

        setCampos((prev) =>
            prev.map((c) =>
                c._key === key && !c.opciones.includes(texto)
                    ? { ...c, opciones: [...c.opciones, texto] }
                    : c
            )
        );
        setOpcionTexto((prev) => ({ ...prev, [key]: "" }));
    };

    const onQuitarOpcion = (key, opcion) => {
        setCampos((prev) =>
            prev.map((c) =>
                c._key === key ? { ...c, opciones: c.opciones.filter((o) => o !== opcion) } : c
            )
        );
    };

    const hayCambiosInvalidos = useMemo(() => {
        return campos.some((c) => {
            if (!c.etiqueta.trim()) return true;
            if (c.tipo === "lista" && c.opciones.length === 0) return true;
            return false;
        });
    }, [campos]);

    const onGuardar = async () => {
        if (hayCambiosInvalidos) {
            Swal.fire(
                "Aviso",
                "Cada campo necesita una etiqueta, y los de tipo Lista desplegable necesitan al menos una opción",
                "warning"
            );
            return;
        }

        const payload = campos.map(({ campo, etiqueta, tipo, requerido, opciones }) => ({
            campo,
            etiqueta: etiqueta.trim(),
            tipo,
            requerido,
            ...(tipo === "lista" ? { opciones } : {}),
        }));

        try {
            setGuardando(true);
            await request.post("Sala/GuardarCamposExtra", {
                idsala: Number(id),
                camposExtra: JSON.stringify(payload),
                usuario: 1,
            });
            Swal.fire("Correcto", "Campos extra guardados correctamente", "success");
        } catch {
            Swal.fire("Error", "No se pudieron guardar los campos extra", "error");
        } finally {
            setGuardando(false);
        }
    };

    return (
        <PlantillaScreen
            titulo={"CONFIGURAR CAMPOS EXTRA"}
            className_main={1}
            className={"seguimiento-actividades-contenedor"}
        >
            <LoaderScreen loader={loader} />

            <div className="csce-contenido">

                <div className="csce-intro">
                    Define aquí los campos adicionales que el usuario deberá llenar al levantar un
                    ticket en <strong>{nombreSala || `la sala #${id}`}</strong>. No requiere
                    desarrollo: se guardan y aplican de inmediato.
                </div>

                {campos.length === 0 && (
                    <div className="csce-vacio">Esta sala no tiene campos extra configurados todavía.</div>
                )}

                {campos.map((c, index) => (
                    <div className="csce-card" key={c._key}>
                        <div className="csce-card-top">
                            <span className="csce-card-numero">{index + 1}</span>
                            <DeleteOutlineIcon
                                className="csce-card-quitar"
                                onClick={() => onQuitarCampo(c._key)}
                            />
                        </div>

                        <div className="csce-card-grid">
                            <div className="lead-form-group">
                                <label>Etiqueta (lo que ve el usuario)</label>
                                <input
                                    type="text"
                                    value={c.etiqueta}
                                    onChange={(e) => onCambiarEtiqueta(c._key, e.target.value)}
                                    placeholder="Ej. Banco"
                                    maxLength={60}
                                />
                            </div>

                            <div className="lead-form-group">
                                <label>Tipo de campo</label>
                                <SimpleSelect
                                    options={TIPO_OPCIONES}
                                    value={c.tipo}
                                    onChange={(val) => onCambiarTipo(c._key, val)}
                                />
                            </div>
                        </div>

                        <label className="csce-requerido">
                            <input
                                type="checkbox"
                                checked={c.requerido}
                                onChange={(e) => onCambiarRequerido(c._key, e.target.checked)}
                            />
                            Obligatorio
                        </label>

                        {c.tipo === "lista" && (
                            <div className="lead-form-group csce-opciones">
                                <label>Opciones de la lista</label>
                                <div className="csce-opciones-input">
                                    <input
                                        type="text"
                                        value={opcionTexto[c._key] || ""}
                                        onChange={(e) =>
                                            setOpcionTexto((prev) => ({ ...prev, [c._key]: e.target.value }))
                                        }
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                onAgregarOpcion(c._key);
                                            }
                                        }}
                                        placeholder="Escribe una opción y presiona Enter"
                                    />
                                    <button type="button" onClick={() => onAgregarOpcion(c._key)}>
                                        Agregar
                                    </button>
                                </div>
                                <div className="csce-chips">
                                    {c.opciones.map((op) => (
                                        <span className="csce-chip" key={op}>
                                            {op}
                                            <DeleteOutlineIcon onClick={() => onQuitarOpcion(c._key, op)} />
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                <button type="button" className="csce-agregar-campo" onClick={onAgregarCampo}>
                    <AddIcon /> Agregar campo
                </button>

                <div className="csce-footer">
                    <button
                        className="csce-footer-cancelar"
                        onClick={() => history.push(`/Permisos/CatalogoSala`)}
                    >
                        Cancelar
                    </button>
                    <button className="csce-footer-guardar" onClick={onGuardar} disabled={guardando}>
                        {guardando ? "Guardando..." : "Guardar campos"}
                    </button>
                </div>

            </div>

            <ButtonBack path={"/Permisos/CatalogoSala"} />

        </PlantillaScreen>
    );
};

export default CatSalaCamposExtraScreen;
